import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import './Layout.scss';
import Header from '../header/Header';
import Notification from '../notification/Notification';
import { buildLoginPath } from 'utils/pathBuilder';
import footerLogo from 'assets/images/footer_logo.png';
import { useSession } from 'context/SessionContext';
import ApiClient from 'services/apiClient';
import { useIdleTimeout } from 'hooks/useIdleTimeout';
import { useTranslation } from 'react-i18next';
import { MemberProfile } from 'types/dto/profile';
import { fetchProfile } from 'services/profileService';

const SUPPORTED_LANGS = ['en', 'zh'];

const Layout = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { session, clearSession } = useSession();

  const location = useLocation();

  useIdleTimeout();

  const isValidLang = lang && SUPPORTED_LANGS.includes(lang.toLowerCase());

  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const isActivatedUser = !!session?._tk && session?._st === 'activated' && session?._at === 'password';
  // const isActivatedUser = !!session?._tk && session?._at === 'password';

  useEffect(() => {
    const loadProfile = async () => {
      if (isActivatedUser) {
        try {
          const response = await fetchProfile('/profile');
          setProfile(response);
        } catch (error) {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    };

    loadProfile();
  }, [isActivatedUser]);

  useEffect(() => {
    if (isValidLang && lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, isValidLang]);

  //deal with 403 error
  useEffect(() => {
    // Register the 403 error handler with ApiClient
    const apiClient = ApiClient.getInstance();
    apiClient.setOnForbiddenCallback(() => {
      // Clear session from context
      clearSession();
      // Navigate to login page, replacing history to prevent back-button access
      navigate(buildLoginPath(), { replace: true });
    });

    // Cleanup: unregister the callback when component unmounts
    return () => {
      apiClient.setOnForbiddenCallback(null);
    };
  }, [navigate, clearSession]);

  if (!isValidLang) {
    const shortLang = lang?.split('-')[0].toLowerCase() || '';
    const currentI18n = i18n.language?.toLowerCase();

    // Redirect to 'en' or 'zh' if the language is neither 'en' nor 'zh'.
    const fallbackLang = SUPPORTED_LANGS.includes(shortLang)
      ? shortLang
      : (SUPPORTED_LANGS.includes(currentI18n) ? currentI18n : 'en');

    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments[0] === lang) pathSegments.shift();

    const targetPath = `/${fallbackLang}/${pathSegments.join('/')}${location.search}`.replace(/\/+/g, '/');

    return <Navigate to={targetPath} replace />;
  }

  return (
    <div className="layout-container" data-testid="layout-container">
      <Notification />
      <Header profile={profile}/>

      <div className="content">
        <div className="outlet-container">
          <Outlet context={{ profile }}/>
        </div>
      </div>

      <div className="footer">
        <div className="copyright">
          <span>Copyright©2025 Vogue Laundry Service Limited</span>
        </div>
        <img src={footerLogo} alt="footer logo" className="footer-logo" />
      </div>
    </div>
  );
};

export default Layout;
